import React, { useState, useEffect } from "react";
import AthleteLayout from "../common/AthleteLayout";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import { useNavigate } from "react-router-dom";
import { linearRegression, linearRegressionLine } from "simple-statistics";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import { AthleteHeader } from "../common/AthleteHeader";
import API_URL from "../../api/API";
import bgImg from "../../images/runRocket.jpeg";

export const AthleteRunrocketTestResult = () => {
  const { athlete } = useAthleteAuthContext();
  const navigate = useNavigate();

  const [maxSpeeds, setMaxSpeeds] = useState([0, 0, 0, 0]);
  const [load, setLoad] = useState([0, 5, 10, 15]);
  const [results, setResults] = useState({
    slope: 0,
    rSquared: 0,
    trainingFocus: "",
    training_focus_template_id: "",
    target_speed_technical: 0,
    target_load_technical: 0,
    target_speed_speedstrength: 0,
    target_load_speedstrength: 0,
    target_speed_power: 0,
    target_load_power: 0,
    target_speed_strengthspeed: 0,
    target_load_strengthspeed: 0,
  });
  const [isDataSent, setIsDataSent] = useState(false);
  const [training_template_id, setTrainingTemplateId] = useState("");

  const [showPopup, setShowPopup] = useState(false); // State to show the popup

  function calculateTargetSpeed(ps, step4Speed, trainingFocus) {
    console.log(ps + " - " + step4Speed + " - " + trainingFocus);
    // Check if the Step 4 speed is blank
    if (!step4Speed) {
      return ""; // Return empty string if Step 4 speed is blank
    }

    // Determine the target speed based on the training focus
    let ts = 0;

    switch (trainingFocus) {
      case "Technical":
        ts = ps * 0.9;
        break;
      case "Speed - Strength":
        ts = ps * 0.75;
        break;
      case "Power":
        ts = ps * 0.5;
        break;
      case "Strength - Speed":
        ts = ps * 0.6;
        break;
      default:
        ts = 0;
        return "Invalid training focus";
    }

    // Round up to the nearest tenth
    return Math.round(ts * 10) / 10;
  }

  // Function to compute Target Load based on Training Focus
  const calculateTargetLoad = (trainingFocus, yIntercept, slope) => {
    if (!yIntercept || !slope) {
      return null; // Return null if calculation fails
    }

    // Load multipliers based on training focus
    const focusMultipliers = {
      Technical: 0.9,
      "Speed - Strength": 0.75,
      Power: 0.5,
      "Strength - Speed": 0.4,
    };

    const multiplier = focusMultipliers[trainingFocus];

    if (multiplier === undefined) {
      return null; // Invalid training focus
    }

    // Calculate Target Load
    const targetLoad = Math.round(
      (yIntercept * multiplier - yIntercept) / slope,
    );

    // Ensure max load is 20
    return Math.min(targetLoad, 20);
  };

  // Function to calculate the Y-Intercept using linear regression
  const calculateYIntercept = (maxSpeeds, loads) => {
    if (maxSpeeds.length !== loads.length || maxSpeeds.length === 0) {
      return null; // Return null if data is inconsistent
    }

    const n = maxSpeeds.length;
    const sumX = loads.reduce((a, b) => a + b, 0);
    const sumY = maxSpeeds.reduce((a, b) => a + b, 0);
    const sumXY = maxSpeeds.reduce((sum, y, i) => sum + y * loads[i], 0);
    const sumX2 = loads.reduce((sum, x) => sum + x * x, 0);

    // Calculate slope (m) and intercept (b) for y = mx + b
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
    const yIntercept = (sumY - slope * sumX) / n;

    return { yIntercept, slope };
  };

  const computeR2Manually = (actual, predicted) => {
    const meanActual =
      actual.reduce((sum, value) => sum + value, 0) / actual.length;

    const ssTotal = actual.reduce(
      (sum, value) => sum + Math.pow(value - meanActual, 2),
      0,
    );
    const ssResidual = actual.reduce(
      (sum, value, index) => sum + Math.pow(value - predicted[index], 2),
      0,
    );

    const rSquared = 1 - ssResidual / ssTotal;
    return rSquared;
  };

  const computeRunRocketResults = (loads, maxSpeeds) => {
    const numericLoads = loads.map((load) => Number(load));
    const numericMaxSpeeds = maxSpeeds.map((speed) => Number(speed));

    const data = numericLoads.map((load, index) => [
      load,
      numericMaxSpeeds[index],
    ]);
    const regression = linearRegression(data);
    const regressionLine = linearRegressionLine(regression);

    const predictedSpeeds = numericLoads.map((load) => regressionLine(load));

    console.log("numericMaxSpeeds:", numericMaxSpeeds);
    console.log("Predicted Speeds:", predictedSpeeds);

    try {
      // Manual R-squared calculation
      const rsq = computeR2Manually(numericMaxSpeeds, predictedSpeeds);
      console.log("Manual R-squared:", rsq);

      //compute the peak speed from maxSpeeds array
      const peakSpeed = Math.max(
        maxSpeeds[0],
        maxSpeeds[1],
        maxSpeeds[2],
        maxSpeeds[3],
      );
      console.log("Peak Speed: " + peakSpeed);

      const { yIntercept, slope } = calculateYIntercept(maxSpeeds, loads);
      console.log("YIntercept: " + yIntercept);
      console.log("Slope: " + slope);

      return {
        slope: regression.m.toFixed(3),
        rSquared: Math.round(rsq * 100), // R-squared in percentage

        target_speed_technical: calculateTargetSpeed(
          peakSpeed,
          maxSpeeds[3],
          "Technical",
        ),
        target_load_technical: calculateTargetLoad(
          "Technical",
          yIntercept,
          slope,
        ),

        target_speed_speedstrength: calculateTargetSpeed(
          peakSpeed,
          maxSpeeds[3],
          "Speed - Strength",
        ),
        target_load_speedstrength: calculateTargetLoad(
          "Speed - Strength",
          yIntercept,
          slope,
        ),

        target_speed_strengthspeed: calculateTargetSpeed(
          peakSpeed,
          maxSpeeds[3],
          "Strength - Speed",
        ),
        target_load_strengthspeed: calculateTargetLoad(
          "Strength - Speed",
          yIntercept,
          slope,
        ),

        target_speed_power: calculateTargetSpeed(
          peakSpeed,
          maxSpeeds[3],
          "Power",
        ),
        target_load_power: calculateTargetLoad("Power", yIntercept, slope),
      };
    } catch (error) {
      console.error("Error calculating R-squared:", error);
      return {
        slope: regression.m.toFixed(3),
        rSquared: "Error",
      };
    }
  };

  const getTrainingFocusFromSlope = (slope) => {
    if (slope < -0.8) {
      return "Technical";
    } else if (slope < -0.7) {
      return "Speed - Strength";
    } else if (slope < -0.5) {
      return "Power";
    } else {
      return "Strength - Speed";
    }
  };

  //later make this dynamic API call
  const getTemplateIdByTrainingFocus = (trainingFocus) => {
    const focusMap = {
      Technical: "6797675c133b0f381f45a25b",
      "Speed - Strength": "6797685f133b0f381f45a25d",
      Power: "6797687c133b0f381f45a25e",
      "Strength - Speed": "67976898133b0f381f45a25f",
    };

    return focusMap[trainingFocus] || null; // Return null if the focus is not found
  };

  //check for existing test results if any.
  const fetchTestResults = async () => {
    /* try {
            const response = await fetch(API_URL.GET_ATHLETE_RUNROCKET_TEST_RESULT + athlete.id, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${athlete.token}`,
                },
            });
            
            if (!response.ok) {
               // throw new Error("Failed to fetch test results");
            }
            
            const data = await response.json();
            console.log(data);
              // If test results are found, set session data to values from the database
            if (data.data && data.data.rsquared) {
              console.log("Setting session data from database");
             // sessionStorage.setItem("maxSpeed1", data.data.maxspeed1 || 0);
            //  sessionStorage.setItem("maxSpeed2", data.data.maxspeed2 || 0);
           //   sessionStorage.setItem("maxSpeed3", data.data.maxspeed3 || 0);
             // sessionStorage.setItem("maxSpeed4", data.data.maxspeed4 || 0);
              
              // Update the state with values from the database
               setMaxSpeeds([data.data.maxspeed1, data.data.maxspeed2, data.data.maxspeed3, data.data.maxspeed4].filter(speed => !isNaN(speed)));
            }
            else{
                const maxSpeed1 = parseFloat(sessionStorage.getItem("maxSpeed1")) || 0;
                const maxSpeed2 = parseFloat(sessionStorage.getItem("maxSpeed2")) || 0;
                const maxSpeed3 = parseFloat(sessionStorage.getItem("maxSpeed3")) || 0;
                const maxSpeed4 = parseFloat(sessionStorage.getItem("maxSpeed4")) || 0;
                const speeds = [maxSpeed1, maxSpeed2, maxSpeed3, maxSpeed4].filter((speed) => !isNaN(speed));
                setMaxSpeeds(speeds);
            }


        } catch (error) {
            console.log(error.message);
        }
        */

    //fetch directly the values from the session and we compute results from that and then save computed results in database
    //fetching the existing results from database might not make sense as session data can keep changing
    const maxSpeed1 = parseFloat(sessionStorage.getItem("maxSpeed1")) || 0;
    const maxSpeed2 = parseFloat(sessionStorage.getItem("maxSpeed2")) || 0;
    const maxSpeed3 = parseFloat(sessionStorage.getItem("maxSpeed3")) || 0;
    const maxSpeed4 = parseFloat(sessionStorage.getItem("maxSpeed4")) || 0;

    console.log(
      maxSpeed1 + "-" + maxSpeed2 + "-" + maxSpeed3 + "-" + maxSpeed4,
    );
    //ifall of the speeds are 0 then in that case there is no session data retained. but the user can have completed the test so check the database for the records
    //if it exists.
    if (
      maxSpeed1 === 0 &&
      maxSpeed2 === 0 &&
      maxSpeed3 === 0 &&
      maxSpeed4 === 0
    ) {
      try {
        const response = await fetch(
          API_URL.GET_ATHLETE_RUNROCKET_TEST_RESULT + athlete.id,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${athlete.token}`,
            },
          },
        );

        if (!response.ok) {
          // throw new Error("Failed to fetch test results");
        }
        const data = await response.json();
        console.log(data);
        // If test results are found, set session data to values from the database
        if (data.data && data.data.rsquared) {
          console.log("Setting session data from database");
          sessionStorage.setItem("maxSpeed1", data.data.maxspeed1 || 0);
          sessionStorage.setItem("maxSpeed2", data.data.maxspeed2 || 0);
          sessionStorage.setItem("maxSpeed3", data.data.maxspeed3 || 0);
          sessionStorage.setItem("maxSpeed4", data.data.maxspeed4 || 0);
          // Update the state with values from the database
          setMaxSpeeds(
            [
              data.data.maxspeed1,
              data.data.maxspeed2,
              data.data.maxspeed3,
              data.data.maxspeed4,
            ].filter((speed) => !isNaN(speed)),
          );
        }
      } catch (error) {
        console.log(error.message);
      }
    } else {
      const speeds = [maxSpeed1, maxSpeed2, maxSpeed3, maxSpeed4].filter(
        (speed) => !isNaN(speed),
      );
      setMaxSpeeds(speeds);
    }
  };

  useEffect(() => {
    // First, try fetching the test results from the database
    fetchTestResults();
  }, []);

  useEffect(() => {
    if (maxSpeeds.length > 0 && load.length > 0) {
      const computedResults = computeRunRocketResults(load, maxSpeeds);
      const focus = getTrainingFocusFromSlope(computedResults.slope); // Get initial training focus based on slope
      const newResults = { ...computedResults, trainingFocus: focus };
      setResults(newResults);

      // Check R-squared and decide whether to save or delete
      const rSquaredValue = Math.round(parseFloat(newResults.rSquared));

      if (rSquaredValue >= 95) {
        // Save results only if R-squared is 95% or greater
        saveResultsToServer(newResults);
      } else if (rSquaredValue < 95) {
        // Delete the record only if R-squared is less than 95
        deleteTestResultsFromServer();
      }
    }
  }, [maxSpeeds, load]);

  const saveResultsToServer = async (results) => {
    // Ensure athlete is authenticated
    if (!athlete || !athlete.token) {
      console.error("Athlete is not authenticated.");
      return;
    }

    setTrainingTemplateId(getTemplateIdByTrainingFocus(results.trainingFocus));

    // Prepare the data object to send to the server
    const dataToSend = {
      maxspeed1: maxSpeeds[0],
      maxspeed2: maxSpeeds[1],
      maxspeed3: maxSpeeds[2],
      maxspeed4: maxSpeeds[3],
      load1: load[0],
      load2: load[1],
      load3: load[2],
      load4: load[3],
      rsquared: Math.round(results.rSquared),
      slope: results.slope,
      training_focus: results.trainingFocus,
      training_focus_template_id: getTemplateIdByTrainingFocus(
        results.trainingFocus,
      ), // Replace with actual logic if required
      target_speed_technical: results.target_speed_technical,
      target_load_technical: results.target_load_technical,
      target_speed_speedstrength: results.target_speed_speedstrength,
      target_load_speedstrength: results.target_load_speedstrength,
      target_speed_strengthspeed: results.target_speed_strengthspeed,
      target_load_strengthspeed: results.target_load_strengthspeed,
      target_speed_power: results.target_speed_power,
      target_load_power: results.target_load_power,
    };

    try {
      const response = await fetch(
        API_URL.POST_ATHLETE_RUNROCKET_TEST_RESULT + athlete.id,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${athlete.token}`,
          },
          body: JSON.stringify(dataToSend),
        },
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Test results saved successfully:", result);
        setIsDataSent(true); // Update state to indicate success
      } else {
        const errorData = await response.json();
        console.error(
          "Failed to save test results:",
          errorData.message || response.statusText,
        );
      }
    } catch (error) {
      console.error("Error saving test results:", error);
    }
  };

  const deleteTestResultsFromServer = async () => {
    if (!athlete || !athlete.token) {
      console.error("Athlete is not authenticated.");
      return;
    }

    try {
      // Send DELETE request to remove the test results from the server
      const response = await fetch(
        `${API_URL.DELETE_ATHLETE_RUNROCKET_TEST_RESULT}${athlete.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${athlete.token}`,
          },
        },
      );

      if (response.ok) {
        console.log("Test results deleted successfully.");
      } else {
        const errorData = await response.json();
        console.error(
          "Failed to delete test results:",
          errorData.message || response.statusText,
        );
      }
    } catch (error) {
      console.error("Error deleting test results:", error);
    }
  };

  const isLowRsq = parseFloat(results.rSquared) < 95;

  const handleRetakeTest = () => {
    navigate("/athlete/tools/runrocket/test/step1");
  };

  const handleDataDeleteAndRetakeTest = () => {
    deleteTestResultsFromServer();
    navigate("/athlete/tools/runrocket/test/step1");
  };

  return (
    <AthleteLayout>
      <div className="relative bg-white h-full">
        {/* background image */}
        <div className="absolute inset-0">
          <img src={bgImg} alt="bgImg" className="object-cover w-full h-full" />
          <div className="w-full h-full absolute inset-0 bg-white/80"></div>
        </div>

        {/* Main Content */}
        <div className="relative h-full pb-5 overflow-y-auto">
          {/* Header */}
          <div className="relative w-full max-w-2xl mx-auto px-5 pt-3">
            {isLowRsq ? (
              <div className="bg-black rounded-2xl flex justify-between gap-8 px-4 py-4 mb-5">
                <span className="text-white text-sm xs:text-bas cursor-pointere">
                  Test
                </span>
                <span className="text-white/40 text-sm xs:text-base hover:text-white cursor-pointer transition">
                  Train
                </span>
                <span className="text-white/40 text-sm xs:text-base hover:text-white cursor-pointer transition">
                  Performance
                </span>
              </div>
            ) : (
              <div className="bg-black rounded-2xl flex justify-between gap-8 px-4 py-4 mb-5">
                <span className="text-white text-sm xs:text-base cursor-pointer">
                  Test
                </span>
                <span
                  onClick={() =>
                    navigate(
                      "/athlete/tools/runrocket/training-weeks/" +
                        training_template_id,
                    )
                  }
                  className="text-white/40 text-sm xs:text-base hover:text-white cursor-pointer transition"
                >
                  Train
                </span>
                <span className="text-white/40 text-sm xs:text-base hover:text-white cursor-pointer transition">
                  Performance
                </span>
              </div>
            )}
          </div>

          <div className="relative w-full max-w-2xl mx-auto px-5 pb-5">
            <div className="relative flex justify-between items-center gap-5 mb-14">
              <div
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 cursor-pointer py-1 px-2 rounded-md hover:bg-smock transition"
              >
                <ArrowL />
                <span className="text-black text-sm font-medium">Back</span>
              </div>
              <p className="text-black text-xl xs:text-2xl font-bold">
                Test Results
              </p>

              {/* hidden for title align center */}
              <div className="opacity-0">
                <div className="flex items-center gap-2 cursor-pointer py-1 px-2 rounded-md hover:bg-smock transition">
                  <ArrowL />
                  <span className="text-black text-sm font-normal">Back</span>
                </div>
              </div>
            </div>

            <div className="mx-auto w-fit block mb-16">
              <div style={{ width: 174, height: 174 }}>
                <CircularProgressbarWithChildren
                  strokeWidth={5}
                  value={Math.round(results.rSquared)}
                  styles={buildStyles({
                    textColor: "#000",
                    pathColor: isLowRsq ? "#FF0000" : "#c19e42", // Red if R-squared is low
                    trailColor: "#000",
                  })}
                >
                  <span className="text-black text-4xl">{`${results.rSquared}%`}</span>
                </CircularProgressbarWithChildren>
              </div>
            </div>

            <div>
              <div className="text-center space-y-2 mb-5">
                {/* <p className="block text-grayish text-xs font-medium uppercase">
                  training focus
                </p> */}

                {/* <span className="block text-black text-sm font-medium">
                  {results.trainingFocus}
                </span> */}

                <h2 className="block text-black text-lg xs:text-xl">
                AST Speed Profile™ Test Results
                </h2>
              </div>
              {/* <div>
                <div className="bg-black rounded-2xl relative p-3 flex justify-center items-center divide-x-2 divide-darkYellow">
                  <div className="flex-1 space-y-2 px-1 text-center">
                    <p className="block text-white text-base font-medium">
                      Slope
                    </p>
                    <span className="block text-white text-sm xs:text-base font-light">
                      {results.slope}
                    </span>
                  </div>
                  <div className="flex-1 space-y-2 px-1 text-center">
                    <p className="block text-white text-base font-medium">
                      R-Squared
                    </p>
                    <span className="block text-white text-sm xs:text-base font-light">
                      {results.rSquared}%
                    </span>
                  </div>
                </div>
              </div> */}
              <div>
                <div className="bg-darkYellow rounded-2xl relative p-3 flex justify-center items-center gap-3">
                  <div className="text-center">
                    <span className="block text-black text-sm xs:text-base font-light capitalize pr-3 border-r-2 border-black">
                      training focus
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="block text-black text-sm xs:text-base font-light">
                      {results.trainingFocus}
                    </span>
                  </div>
                </div>
              </div>

              {/* Separate Buttons */}
              <div className="flex justify-center gap-4">
                {isLowRsq ? (
                  <button
                    onClick={handleRetakeTest}
                    className="block w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300 disabled:bg-black/50 mt-14"
                  >
                    Retake Test
                  </button>
                ) : (
                  <div className="w-full max-w-2xl mx-auto">
                    {/* Start Training Button */}
                    <button
                      onClick={() =>
                        navigate(
                          "/athlete/tools/runrocket/training-weeks/" +
                            training_template_id,
                        )
                      }
                      className="block w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300 disabled:bg-black/50 mt-14"
                    >
                      Start Training
                    </button>

                    {/* Redo Training Button */}
                    <button
                      onClick={() => setShowPopup(true)} // Show the popup when clicking Redo Training
                      className="block w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300 disabled:bg-black/50 mt-[24px]"
                    >
                      Re Test
                    </button>

                    {/* Confirmation Popup */}
                    {showPopup && (
                      <div
                        className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center p-5 z-50"
                        onClick={() => setShowPopup(false)} // Close the popup if clicked outside
                      >
                        <div
                          className="bg-white p-5 xs:p-8 rounded-xl shadow-lg max-w-md w-full"
                          onClick={(e) => e.stopPropagation()} // Prevent the popup from closing if clicked inside
                        >
                          <h3 className="text-base xs:text-lg font-medium mb-4">
                            Are you sure you want to re-compute your training
                            focus? All your previous data will be reset.
                          </h3>
                          <div className="flex justify-between">
                            <button
                              onClick={() => setShowPopup(false)} // Close the popup
                              className="border border-gray-400 text-grayish font-medium text-xs xs:text-sm !leading-none px-6 py-3 rounded-lg hover:bg-grayish/10 transition"
                            >
                              No
                            </button>
                            <button
                              onClick={handleDataDeleteAndRetakeTest} // Call function to delete test results
                              className="px-6 py-3 text-white text-xs xs:text-sm !leading-none bg-black hover:bg-black/70 rounded-lg transition-all duration-300 disabled:bg-black/50"
                            >
                              Yes
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};

const ArrowL = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path
      d="M7 13L1 7L7 1"
      stroke="#000"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
